import { yupResolver } from "@hookform/resolvers/yup";
import { getJobDriveSlotsApi } from "api/services/jobs";
import { updateJobApplicantSlotApi } from "api/services/resume";
import { Button } from "components/Button";
import EssFullWidthModal from "components/modelpopup/baseModal";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { Bounce, toast } from "react-toastify";
import { customStyleSelect } from "utils/common";
import * as Yup from "yup";

const SelectSlotModal = ({
  open,
  onCancel,
  applicant,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const slotSchema = Yup.object().shape({
    slot_id: Yup.object().required("Please select a time slot"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(slotSchema),
    defaultValues: {
      slot_id: null,
    },
  });

  useEffect(() => {
    if (open && applicant?.job?.id) {
      fetchSlots();
      reset();
    }
  }, [open, applicant, reset]);

  const fetchSlots = async () => {
    if (!applicant?.job?.id) return;
    
    setSlotsLoading(true);
    try {
      const response = await getJobDriveSlotsApi({ job_id: applicant.job.id });
      // Handle different response structures
      const slotsData = response.data;
      let slotsArray = [];
      if (Array.isArray(slotsData)) {
        slotsArray = slotsData;
      } else if (slotsData?.results && Array.isArray(slotsData.results)) {
        slotsArray = slotsData.results;
      } else if (slotsData?.slots && Array.isArray(slotsData.slots)) {
        slotsArray = slotsData.slots;
      }
      // Filter only active slots
      const activeSlots = slotsArray.filter(slot => slot.status === "active");
      setSlots(activeSlots);
      
      // Prefill with currently selected slot if exists
      if (applicant?.job_drive_invitation?.slot?.id) {
        const currentSlotId = applicant.job_drive_invitation.slot.id;
        const currentSlot = activeSlots.find(slot => slot.id === currentSlotId);
        if (currentSlot) {
          // Create the option object to match slotOptions format
          let optionLabel = currentSlot.title || `Slot ${currentSlot.id}`;
          if (currentSlot.start_at && currentSlot.end_at) {
            try {
              const startDate = parseISO(currentSlot.start_at);
              const endDate = parseISO(currentSlot.end_at);
              const isFull = (currentSlot.invited_count || 0) >= currentSlot.capacity;
              optionLabel = `${currentSlot.title || "Slot"} - ${format(startDate, "dd-MM-yyyy HH:mm")} - ${format(endDate, "HH:mm")} (${currentSlot.invited_count || 0}/${currentSlot.capacity}${isFull ? " - Full" : ""})`;
            } catch (error) {
              console.error("Error parsing dates:", error);
            }
          }
          setValue("slot_id", {
            label: optionLabel,
            value: currentSlot.id,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Failed to load slots", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setSlotsLoading(false);
    }
  };

  const slotOptions = slots?.map(slot => {
    if (slot.start_at && slot.end_at) {
      const startDate = parseISO(slot.start_at);
      const endDate = parseISO(slot.end_at);
      const isFull = (slot.invited_count || 0) >= slot.capacity;
      const label = `${slot.title || "Slot"} - ${format(startDate, "dd-MM-yyyy HH:mm")} - ${format(endDate, "HH:mm")} (${slot.invited_count || 0}/${slot.capacity}${isFull ? " - Full" : ""})`;
      return {
        label,
        value: slot.id,
        disabled: isFull,
      };
    }
    return {
      label: slot.title || `Slot ${slot.id}`,
      value: slot.id,
      disabled: (slot.invited_count || 0) >= slot.capacity,
    };
  }) || [];

  const onSubmit = async (data) => {
    if (!applicant?.id) return;
    
    setIsLoading(true);
    try {
      // Update job applicant with slot and status
      await updateJobApplicantSlotApi(applicant.id, {
        slot_id: data.slot_id.value,
        status: "invited"
      });
      
      toast.success("Time slot assigned and status updated successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      
      reset();
      if (onSuccess) onSuccess();
      onCancel();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || error.message || "Failed to assign slot";
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onCancel();
  };

  return (
    <EssFullWidthModal
      mask={false}
      title="Select Job Drive Time Slot"
      open={open}
      width="40%"
      footer={[]}
      onCancel={handleClose}
      className="full-width-modal"
    >
      <div className="modal-content">
        <div className="modal-body">
          {applicant && (
            <div className="mb-3 p-3" style={{ background: "#f8f9fa", borderRadius: "8px" }}>
              <p className="mb-1"><strong>Applicant:</strong> {applicant.first_name} {applicant.last_name}</p>
              <p className="mb-0"><strong>Email:</strong> {applicant.email}</p>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Select Time Slot <span className="text-danger">*</span></label>
                  <Controller
                    name="slot_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        placeholder={slotsLoading ? "Loading slots..." : "Select a time slot"}
                        onChange={field.onChange}
                        value={field.value}
                        options={slotOptions}
                        styles={customStyleSelect}
                        isDisabled={isLoading || slotsLoading}
                        isLoading={slotsLoading}
                      />
                    )}
                  />
                  {errors.slot_id && (
                    <small className="text-danger">{errors.slot_id?.message}</small>
                  )}
                  {slots.length === 0 && !slotsLoading && (
                    <small className="text-muted">No active slots available for this job.</small>
                  )}
                </div>
              </div>
            </div>
            <div className="submit-section">
              <Button
                loading={isLoading}
                disabled={isLoading || slotsLoading}
                className="btn btn-primary submit-btn"
                type="submit"
                label="Save"
              />
            </div>
          </form>
        </div>
      </div>
    </EssFullWidthModal>
  );
};

export default SelectSlotModal;
